import { getImageUrl } from './api';

/**
 * Extracts src attribute if user provided an <iframe> embed snippet
 */
export const extractIframeSrc = (input?: string): string => {
    if (!input) return '';
    const trimmed = input.trim();
    if (trimmed.includes('<iframe')) {
        const match = trimmed.match(/src=["']([^"']+)["']/i);
        if (match && match[1]) return match[1];
    }
    return trimmed;
};

/**
 * Checks if URL is a direct video file (mp4, webm, etc.)
 */
export const isDirectVideoFile = (url?: string): boolean => {
    if (!url) return false;
    const cleanUrl = url.split('?')[0].toLowerCase();
    return cleanUrl.endsWith('.mp4') || cleanUrl.endsWith('.webm') || cleanUrl.endsWith('.ogg') || cleanUrl.endsWith('.mov') || cleanUrl.endsWith('.mkv');
};

/**
 * Checks if URL is a dummy placeholder that shouldn't be rendered as a broken iframe
 */
export const isDummyUrl = (url?: string): boolean => {
    if (!url) return true;
    const trimmed = url.trim().toLowerCase();
    if (!trimmed || trimmed === 'sample' || trimmed === 'sample2' || trimmed === 'test' || trimmed === 'placeholder') return true;
    if (trimmed.includes('youtube.com/watch?v=sample') || trimmed.includes('youtu.be/sample')) return true;
    return false;
};

/**
 * Converts various video & tour URLs into embeddable iframe URLs
 */
export const getEmbedUrl = (rawUrl?: string): string => {
    if (!rawUrl) return '';
    const url = extractIframeSrc(rawUrl);
    if (isDummyUrl(url)) return '';

    try {
        // YouTube Shorts
        if (url.includes('youtube.com/shorts/')) {
            const id = url.split('shorts/')[1]?.split(/[?&/]/)[0];
            return id ? `https://www.youtube.com/embed/${id}?rel=0` : url;
        }
        // YouTube youtu.be/ID
        if (url.includes('youtu.be/')) {
            const id = url.split('youtu.be/')[1]?.split(/[?&/]/)[0];
            return id ? `https://www.youtube.com/embed/${id}?rel=0` : url;
        }
        // YouTube watch?v=ID
        if (url.includes('youtube.com/watch')) {
            const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
            const id = parsed.searchParams.get('v');
            return id ? `https://www.youtube.com/embed/${id}?rel=0` : url;
        }
        // YouTube embed
        if (url.includes('youtube.com/embed/')) {
            return url;
        }
        // Vimeo
        if (url.includes('vimeo.com/') && !url.includes('player.vimeo.com')) {
            const id = url.split('vimeo.com/')[1]?.split(/[?&/]/)[0];
            return id ? `https://player.vimeo.com/video/${id}` : url;
        }
        // Kuula 360
        if (url.includes('kuula.co/post/')) {
            return url.replace('kuula.co/post/', 'kuula.co/share/');
        }
        // Google Earth / Matterport / other embed links
        return url;
    } catch {
        return url;
    }
};

